import type { Reducer } from 'react';
import type { ClientFlowActionContactDetails } from '~/graphql/types.client';
import type { Flow___Argument_PointerFragment } from '~/graphql/types';
import getActiveField from '../utils/getActiveField';
import type { EditorValue } from '~/components/organism/PluginsEditor/types';

export type FieldName = Exclude<
  keyof ClientFlowActionContactDetails['field'],
  '__typename' | 'phoneValue' | 'nameValue'
>;
export type State = {
  selectedField: FieldName;
  action: ClientFlowActionContactDetails;
};

type UpdateNameAction = {
  type: 'updateName';
  payload: EditorValue;
};

type UpdatePhoneAction = {
  type: 'updatePhone';
  payload: EditorValue;
};

type UpdateAddressAction = {
  type: 'updateAddress';
  /** The address must always be a pointer to an address-typed flow value */
  payload: Flow___Argument_PointerFragment;
};

export type UpdateValueAction =
  | UpdateNameAction
  | UpdatePhoneAction
  | UpdateAddressAction;

export type SelectFieldAction = {
  type: 'selectField';
  payload: FieldName;
};

export type ReducerAction = UpdateValueAction | SelectFieldAction;

export const reducer: Reducer<State, ReducerAction> = (state, action) => {
  switch (action.type) {
    case 'selectField': {
      // Because we don't allow users to set `unsubscribeEmail` to false
      // when they select the field we set it to true for them.
      // This way it can only ever be true or undefined
      if (action.payload === 'unsubscribeEmail') {
        return {
          ...state,
          selectedField: action.payload,
          action: {
            ...state.action,
            field: {
              ...state.action.field,
              unsubscribeEmail: true,

              // Reset other fields
              phoneValue: [],
              nameValue: [],
              address: undefined,
            },
          },
        };
      }

      return {
        ...state,
        selectedField: action.payload,

        action: {
          ...state.action,

          // Reset the fields
          field: {
            ...state.action.field,
            phoneValue: [],
            nameValue: [],
            unsubscribeEmail: undefined,
            address: undefined,
          },
        },
      };
    }

    case 'updateName': {
      return {
        ...state,
        action: {
          ...state.action,
          field: {
            ...state.action.field,
            nameValue: action.payload,

            // Reset other fields
            phoneValue: [],
            unsubscribeEmail: undefined,
            address: undefined,
          },
        },
      };
    }
    case 'updatePhone': {
      return {
        ...state,
        action: {
          ...state.action,
          field: {
            ...state.action.field,
            phoneValue: action.payload,

            // Reset other fields
            nameValue: [],
            unsubscribeEmail: undefined,
            address: undefined,
          },
        },
      };
    }
    case 'updateAddress': {
      return {
        ...state,
        action: {
          ...state.action,
          field: {
            ...state.action.field,
            address: action.payload,

            // Reset other fields
            name: null,
            phone: null,
            nameValue: [],
            phoneValue: [],
            unsubscribeEmail: undefined,
          },
        },
      };
    }

    default:
      return state;
  }
};

export const createInitialState = (state: State): State => ({
  ...state,
  selectedField: getActiveField(state.action),
});
